import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Users, UserCircle, CalendarDays, Activity, Clock, TrendingUp } from "lucide-react";
import api from "../lib/api";
import PageHeader from "../components/PageHeader";
import CatMascot from "../components/CatMascot";
import { useAuth } from "../context/AuthContext";
import { Skeleton } from "../components/ui/skeleton";

const StatCard = ({ icon: Icon, label, value, color, delay }) => (
  <motion.div
    initial={{ opacity: 0, y: 16 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ delay }}
    className="card-glass rounded-2xl p-5 transition-all hover:-translate-y-1 hover:shadow-[0_0_24px_rgba(139,92,246,0.2)]"
    data-testid={`stat-${label.toLowerCase().replace(/ /g, "-")}`}
  >
    <div className="flex items-center justify-between">
      <span className="text-xs font-semibold uppercase tracking-wider text-slate-400">{label}</span>
      <div className={`rounded-lg p-2 ${color}`}><Icon className="h-4 w-4" /></div>
    </div>
    <p className="mt-3 font-display text-3xl font-black text-white">{value}</p>
  </motion.div>
);

export default function Dashboard() {
  const [stats, setStats] = useState(null);
  const { user } = useAuth();
  const navigate = useNavigate();

  useEffect(() => { api.get("/dashboard/stats").then((r) => setStats(r.data)); }, []);

  return (
    <div>
      <PageHeader title={`Welcome back, ${user?.first_name || "Staff"}`} subtitle="Here's what's happening across the Kingdom today." />

      {!stats ? (
        <div className="grid grid-cols-2 gap-5 lg:grid-cols-5">
          {[...Array(5)].map((_, i) => <Skeleton key={i} className="h-28 rounded-2xl bg-slate-800/50" />)}
        </div>
      ) : (
        <div className="grid grid-cols-2 gap-5 lg:grid-cols-5">
          <StatCard icon={UserCircle} label="Staff" value={stats.staff_count} color="bg-purple-500/15 text-purple-400" delay={0} />
          <StatCard icon={Users} label="Customers" value={stats.customer_count} color="bg-indigo-500/15 text-indigo-400" delay={0.05} />
          <StatCard icon={CalendarDays} label="Events" value={stats.event_count} color="bg-emerald-500/15 text-emerald-400" delay={0.1} />
          <StatCard icon={Clock} label="Upcoming" value={stats.upcoming_events} color="bg-amber-500/15 text-amber-400" delay={0.15} />
          <StatCard icon={Activity} label="Active Sessions" value={stats.active_sessions} color="bg-red-500/15 text-red-400" delay={0.2} />
        </div>
      )}

      <div className="mt-8 grid grid-cols-1 gap-6 lg:grid-cols-3">
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.25 }} className="card-glass rounded-2xl p-6 lg:col-span-2">
          <div className="mb-4 flex items-center gap-2">
            <TrendingUp className="h-5 w-5 text-purple-400" />
            <h2 className="font-heading text-lg font-semibold text-white">Recent Events</h2>
          </div>
          {stats?.recent_events?.length ? (
            <div className="space-y-2">
              {stats.recent_events.map((e) => (
                <button
                  key={e.id}
                  data-testid={`recent-event-${e.id}`}
                  onClick={() => navigate(`/events/${e.id}`)}
                  className="flex w-full items-center justify-between rounded-xl border border-slate-800/60 bg-slate-900/40 px-4 py-3 text-left transition-all hover:border-purple-500/40 hover:bg-slate-800/60"
                >
                  <div>
                    <p className="font-semibold text-white">{e.title}</p>
                    <p className="text-xs text-slate-400">{new Date(e.scheduled_at).toLocaleString()}</p>
                  </div>
                  <span className="rounded-full border border-purple-500/30 bg-purple-500/10 px-2.5 py-1 text-xs font-medium capitalize text-purple-300">{e.category}</span>
                </button>
              ))}
            </div>
          ) : (
            <p className="py-8 text-center text-sm text-slate-500">No events yet. Schedule your first event!</p>
          )}
        </motion.div>

        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.3 }} className="card-glass relative flex flex-col items-center justify-center overflow-hidden rounded-2xl p-6 text-center">
          <div className="pointer-events-none absolute inset-0 bg-purple-700/10 blur-3xl" />
          <motion.div animate={{ y: [0, -10, 0] }} transition={{ repeat: Infinity, duration: 3 }} className="relative z-10">
            <CatMascot size={72} />
          </motion.div>
          <p className="relative z-10 mt-4 font-display text-xl font-black text-white">The Kingdom is thriving</p>
          <p className="relative z-10 mt-1 text-xs text-slate-400">Server ID 1477250238548017336</p>
        </motion.div>
      </div>
    </div>
  );
}
